import React from "react";
import { GraduationCap, Briefcase, CheckCircle2, Trophy } from "lucide-react";
import { EDUCATION, EXPERIENCE } from "@/lib/portfolioData";
import SectionHeader from "@/components/SectionHeader";
import Reveal from "@/components/Reveal";

export default function ExperienceTimeline() {
  return (
    <section
      id="experience"
      className="relative py-24 md:py-36 px-6 md:px-10 bg-[#0d0d0d]"
      data-testid="experience-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          number="02"
          tag="Journey"
          title={<>
            Education &<br />
            <span className="text-gradient-gold">Experience</span>
          </>}
          kicker="A short, sharp timeline of the milestones that shape my craft."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Education */}
          <div className="lg:col-span-5" data-testid="education-block">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h3 className="font-display uppercase text-sm tracking-[0.3em] text-white/80">
                Education
              </h3>
            </div>

            <div className="relative pl-8 border-l border-[#D4AF37]/25 space-y-10">
              {EDUCATION.map((ed, i) => (
                <Reveal key={i} delay={i * 100}>
                  <div className="relative">
                    <span className="absolute -left-[42px] top-1 w-3 h-3 rounded-full bg-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.6)]" />
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2">
                      Graduated {ed.year}
                    </p>
                    <h4 className="font-display text-2xl text-white mb-1">{ed.degree}</h4>
                    <p className="text-[#B3B3B3] text-sm mb-4">{ed.school}</p>
                    <p className="text-white/70 text-sm leading-relaxed">{ed.detail}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div className="lg:col-span-7" data-testid="experience-block">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h3 className="font-display uppercase text-sm tracking-[0.3em] text-white/80">
                Experience
              </h3>
            </div>

            <div className="relative pl-8 border-l border-[#D4AF37]/25 space-y-10">
              {EXPERIENCE.map((ex, i) => (
                <Reveal key={i} delay={i * 100}>
                  <div className="relative">
                    <span className="absolute -left-[42px] top-1 w-3 h-3 rounded-full bg-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.6)]" />
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2">
                      {ex.period}
                    </p>
                    <h4 className="font-display text-2xl md:text-3xl text-white mb-1 leading-tight">
                      {ex.role}
                    </h4>
                    <p className="text-[#B3B3B3] text-sm mb-6">{ex.company}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="glass rounded-xl p-5 raise-hover">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-3">
                          Responsibilities
                        </p>
                        <ul className="space-y-2.5">
                          {ex.responsibilities.map((r) => (
                            <li key={r} className="flex items-start gap-2 text-sm text-white/80">
                              <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="glass rounded-xl p-5 raise-hover border-[#D4AF37]/20">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-3 flex items-center gap-2">
                          <Trophy className="w-3 h-3" /> Achievements
                        </p>
                        <ul className="space-y-2.5">
                          {ex.achievements.map((a) => (
                            <li key={a} className="flex items-start gap-2 text-sm text-white/85">
                              <span className="text-[#D4AF37] mt-0.5">◆</span>
                              {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
